import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const STATUSES = [
  { key: "present", label: "حاضر", color: "bg-emerald-600 hover:bg-emerald-700", icon: CheckCircle2 },
  { key: "absent", label: "غائب", color: "bg-rose-600 hover:bg-rose-700", icon: XCircle },
  { key: "excused", label: "بعذر", color: "bg-amber-500 hover:bg-amber-600", icon: AlertCircle },
];

export default function AttendancePage() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [children, setChildren] = useState([]);
  const [records, setRecords] = useState([]);
  const canMark = user.role === "admin" || user.role === "teacher";

  const load = async () => {
    const [{ data: ch }, { data: rec }] = await Promise.all([
      api.get("/children"),
      api.get(`/attendance?date=${date}`),
    ]);
    setChildren(ch);
    setRecords(rec);
  };
  useEffect(() => { load(); }, [date]);

  const statusMap = useMemo(() => {
    const m = {};
    records.forEach((r) => { m[r.child_id] = r.status; });
    return m;
  }, [records]);

  const mark = async (child_id, status) => {
    try {
      await api.post("/attendance", { child_id, date, status, note: "" });
      toast.success(status === "absent" ? "تم التسجيل — إشعار واتساب للأهل" : "تم التسجيل");
      await load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">الحضور والغياب</h1>
          <p className="text-slate-500 mt-2">تسجيل بضغطة واحدة — يُرسل إشعار واتساب للأهل عند الغياب</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user.role === "admin" && (
            <Button
              data-testid="export-xlsx-btn"
              onClick={async () => {
                const month = date.slice(0, 7);
                try {
                  const res = await api.get(`/attendance/export.xlsx?month=${encodeURIComponent(month)}`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `الحضور-${month}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 5000);
                } catch (e) { toast.error(extractError(e)); }
              }}
              variant="outline"
              className="h-11 gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            >
              <Download className="w-4 h-4" /> تصدير Excel للشهر
            </Button>
          )}
          <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-slate-200">
            <span className="text-sm font-bold text-slate-700">التاريخ:</span>
            <Input data-testid="attendance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-10" />
          </div>
        </div>
      </div>

      {children.length === 0 ? (
        <Card><CardContent className="p-16 text-center text-slate-400">لا يوجد أطفال</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((c) => {
            const st = statusMap[c.id];
            return (
              <Card key={c.id} data-testid={`attendance-row-${c.id}`} className="border-slate-200/70 card-elevated">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-emerald-400 text-white font-black text-lg flex items-center justify-center">
                      {c.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">{c.disability_type}</div>
                    </div>
                    {st && <StatusBadge status={st} />}
                  </div>
                  {canMark ? (
                    <div className="grid grid-cols-3 gap-2">
                      {STATUSES.map((s) => {
                        const Icon = s.icon;
                        const active = st === s.key;
                        return (
                          <Button
                            key={s.key}
                            size="sm"
                            data-testid={`mark-${s.key}-${c.id}`}
                            onClick={() => mark(c.id, s.key)}
                            className={`h-11 gap-1 text-white font-bold ${s.color} ${active ? "ring-2 ring-offset-2 ring-slate-400" : "opacity-80"}`}
                          >
                            <Icon className="w-4 h-4" />
                            {s.label}
                          </Button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 text-center py-2">
                      {st ? "تم تسجيل الحضور اليوم" : "لم يُسجل بعد"}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    present: { label: "حاضر", cls: "bg-emerald-100 text-emerald-700" },
    absent: { label: "غائب", cls: "bg-rose-100 text-rose-700" },
    excused: { label: "بعذر", cls: "bg-amber-100 text-amber-700" },
  };
  const s = map[status] || {};
  return <Badge className={`${s.cls} hover:${s.cls}`}>{s.label}</Badge>;
}
